import React from 'react';
import { Tabs, Card } from 'antd';
import { FileTextOutlined, FileOutlined, GiftOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../contexts/PermissionContext';
import InvoicesTable from './InvoicesTable';
import ReceiptsTable from './ReceiptsTable';
import CustomerLoyaltyPoints from './CustomerLoyaltyPoints';
import CustomerProducts from './CustomerProducts';

const { TabPane } = Tabs;

interface CustomerTabsProps {
  customer: any;
  invoices: any[];
  receipts: any[];
  invoicesLoading: boolean;
  receiptsLoading: boolean;
  invoicesPage: number;
  receiptsPage: number;
  invoicesTotal: number;
  receiptsTotal: number;
  onInvoicesPageChange: (page: number) => void;
  onReceiptsPageChange: (page: number) => void;
  onCustomerUpdate?: () => void;
}

const CustomerTabs: React.FC<CustomerTabsProps> = ({
  customer,
  invoices,
  receipts,
  invoicesLoading,
  receiptsLoading,
  invoicesPage,
  receiptsPage,
  invoicesTotal,
  receiptsTotal,
  onInvoicesPageChange,
  onReceiptsPageChange,
  onCustomerUpdate,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  // Check permissions for each tab
  const canViewProducts =
    hasPermission('view_customer_products') ||
    hasPermission('view_products') ||
    hasPermission('products.read') ||
    hasPermission('products.view') ||
    hasPermission('customers.view_products');

  const canViewInvoices =
    hasPermission('view_invoices') ||
    hasPermission('invoices.read') ||
    hasPermission('invoices.view') ||
    hasPermission('customers.view_invoices');

  const canViewReceipts =
    hasPermission('view_receipts') ||
    hasPermission('receipts.read') ||
    hasPermission('receipts.view') ||
    hasPermission('customers.view_receipts');

  const canViewLoyaltyPoints =
    hasPermission('view_loyalty_point') ||
    hasPermission('view_loyalty_points') ||
    hasPermission('loyalty_points.read') ||
    hasPermission('loyalty_points.view') ||
    hasPermission('customers.view_loyalty_points');

  // Determine default active tab based on permissions
  const getDefaultActiveKey = () => {
    if (canViewProducts) return 'products';
    if (canViewInvoices) return 'invoices';
    if (canViewReceipts) return 'receipts';
    if (canViewLoyaltyPoints) return 'loyalty';
    return 'products'; // fallback
  };

  return (
    <Card>
      <Tabs defaultActiveKey={getDefaultActiveKey()}>
        {canViewProducts && (
          <TabPane
            tab={
              <span>
                <ShoppingOutlined />
                {t('customers.tabs.products', 'Products')}
              </span>
            }
            key="products"
          >
            <CustomerProducts customer={customer} onUpdate={onCustomerUpdate} />
          </TabPane>
        )}

        {canViewInvoices && (
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                {t('customers.tabs.invoices', 'Invoices')}
              </span>
            }
            key="invoices"
          >
            <InvoicesTable
              invoices={invoices}
              loading={invoicesLoading}
              currentPage={invoicesPage}
              pageSize={10}
              total={invoicesTotal}
              onPageChange={onInvoicesPageChange}
            />
          </TabPane>
        )}

        {canViewReceipts && (
          <TabPane
            tab={
              <span>
                <FileOutlined />
                {t('customers.tabs.receipts', 'Receipts')}
              </span>
            }
            key="receipts"
          >
            <ReceiptsTable
              receipts={receipts}
              loading={receiptsLoading}
              currentPage={receiptsPage}
              pageSize={10}
              total={receiptsTotal}
              onPageChange={onReceiptsPageChange}
            />
          </TabPane>
        )}

        {canViewLoyaltyPoints && (
          <TabPane
            tab={
              <span>
                <GiftOutlined />
                {t('customers.tabs.loyalty', 'Loyalty Points')}
              </span>
            }
            key="loyalty"
          >
            <CustomerLoyaltyPoints customer={customer} onUpdate={onCustomerUpdate} />
          </TabPane>
        )}
      </Tabs>
    </Card>
  );
};

export default CustomerTabs;
